import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Grid, TextField, Button, Box, IconButton, Tooltip, Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import {
  PageContainer, HeaderTitle,
  SummaryBox, SummaryRow, SummaryValue, SystemField, SummaryLabel,
} from "./OrderSummary.styles";
import { updateOrderShipping, updateOrderTax, updateOrderShippingBaseCost, updateOrderShippingAdditionalCost, calculateOrderShipping, getOrderById, getOrderShipments } from "../../api/api";
import { useToast } from "../../components/ToastProvider/ToastProvider";
import ShipmentDetails from "../ShipmentDetails/ShipmentDetails"; 

function toMoneyInput(n, fallback = 0) {
  const num = Number.isFinite(Number(n)) ? Number(n) : Number(fallback || 0);
  return num.toFixed(2);
}
const num = (v) => Number(v || 0);
const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

const fallbackProducts = [];

export default function OrderSummary({ order, onComplete, onOrderChanged, onEditingChange, onBillableChange, disableButtons = false, readOnly = false }) {
  const { showToast } = useToast(); 

  const [newShippingAmount, setNewShippingAmount] = useState("");
  const [updatingShipping, setUpdatingShipping] = useState(false);
  const [newTaxAmount, setNewTaxAmount] = useState("");
  const [updatingTax, setUpdatingTax] = useState(false);
  const [newShippingBaseCost, setNewShippingBaseCost] = useState("");
  const [updatingShippingBaseCost, setUpdatingShippingBaseCost] = useState(false);
  const [newShippingAdditionalCost, setNewShippingAdditionalCost] = useState("");
  const [updatingShippingAdditionalCost, setUpdatingShippingAdditionalCost] = useState(false);
  const [calculatedShipping, setCalculatedShipping] = useState(null);
  const [shippingBreakdown, setShippingBreakdown] = useState(null);
  const [orderShipmentCount, setOrderShipmentCount] = useState(null);
  const [shipments, setShipments] = useState([]);
  
  // Check if order is shipped or delivered - buttons should be disabled
  const isShippedOrDelivered = order?.status === 'SHIPPED' || order?.status === 'DELIVERED';

  // Fetch shipments to calculate total shipping + additional costs
  const fetchShipmentsForSummary = async () => {
    if (order?.id) {
      try {
        console.log(`[OrderSummary] Fetching shipments for order ${order.id}`);
        const data = await getOrderShipments(order.id);
        console.log(`[OrderSummary] Fetched ${data?.length || 0} shipments:`, data?.map(s => ({
          id: s.id, 
          shipping_base_cost: s.shipping_base_cost, 
          shipping_additional_cost: s.shipping_additional_cost,
          additional_cost: s.additional_cost,
          buyer_shipping_value: s.buyer_shipping_value
        })));
        // Force state update by creating a new array reference
        setShipments([...(data || [])]);
      } catch (error) {
        console.error('Error fetching shipments:', error);
      }
    }
  };

  useEffect(() => {
    fetchShipmentsForSummary();
  }, [order?.id]);

  // Track if user is editing shipping, tax, or shipping cost fields
  const isEditing = useMemo(() => {
    return newShippingAmount !== "" || newTaxAmount !== "" || newShippingBaseCost !== "" || newShippingAdditionalCost !== "";
  }, [newShippingAmount, newTaxAmount, newShippingBaseCost, newShippingAdditionalCost]);

  // Notify parent when editing state changes
  useEffect(() => {
    if (onEditingChange) {
      onEditingChange(isEditing);
    }
  }, [isEditing, onEditingChange]);

  const items = useMemo(() => {
    if (order?.items?.length) {
      // Filter only available products AND exclude items with order_item_status === 'exclude'
      const availableItems = order.items.filter(item => 
        item.availability === 'available' && 
        (item.order_item_status !== 'exclude')
      );
      
      return availableItems.map((it, idx) => {
        const p = it.product || {}; // Get product data from order item
        // Check if item is ON_DEMAND
        // Item is ON_DEMAND if pricing_mode is ON_DEMAND AND unit_price is 0/null/undefined
        // If unit_price > 0, it means price is confirmed, so include in calculations
        const unitPriceNum = Number(it.unit_price ?? it.product_price ?? 0);
        const isOnDemand = (it.pricing_mode === 'ON_DEMAND' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined)) ||
                           (it.pricing_mode !== 'FIXED' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined));
        return {
          id: it.id ?? idx,
          img: it.snapshot_product_image || p.image_url || p.image_urls?.[0] || "https://via.placeholder.com/50",
          mfr: it.snapshot_product_brand || p.brand || "—",
          description: it.snapshot_product_description || it.product_name || p.description || "—",
          location: it.snapshot_product_location || p.location || "—",
          category: it.snapshot_product_category || p.category || "—",
          condition: it.snapshot_product_condition || p.condition || "New",
          qty: it.quantity ?? 1,
          internal: it.snapshot_product_sku || it.product_sku || p.sku || "—",
          unitPrice: it.unit_price ?? it.product_price ?? 0,
          total: (it.quantity ?? 1) * (it.unit_price ?? it.product_price ?? 0),
          isOnDemand: isOnDemand, // Flag for ON_DEMAND items
          _unit: num(it.unit_price ?? it.product_price),
          _qty: num(it.quantity ?? 1),
          _disc: num(it.discount_amount),
          _line: num(it.total_price),
          availability: it.availability || 'available', // Add availability for debugging
        };
      });
    }
    return fallbackProducts;
  }, [order]);

  // Check if any item is ON_DEMAND
  const hasOnDemandItems = useMemo(() => {
    return items.some(item => item.isOnDemand);
  }, [items]);

  const computedSubtotal = useMemo(() => {
    if (items.length) {
      // Exclude ON_DEMAND items from subtotal
      const subtotal = round2(
        items.reduce((acc, r) => {
          if (r.isOnDemand) {
            return acc; // Exclude ON_DEMAND items
          }
          return acc + num(r.total);
        }, 0)
      );
      return subtotal;
    }
    const fallbackSubtotal = round2(num(order?.subtotal));
    return fallbackSubtotal;
  }, [items, order?.subtotal]);

  const [systemNote, setSystemNote] = useState(
    "Adjusted billable amount due to request of 10% discount"
  );
  const [subtotal, setSubtotal] = useState(toMoneyInput(computedSubtotal, 0));
  // Admin portal: show shipping_base_cost, fallback to shipping_cost if base_cost is 0 (for old orders)
  const getShippingValue = (order) => {
    if (order?.shipping_base_cost && order.shipping_base_cost > 0) {
      return order.shipping_base_cost;
    }
    // Fallback to shipping_cost for old orders that don't have shipping_base_cost set
    return order?.shipping_cost || 0;
  };
  const [shipping, setShipping] = useState(toMoneyInput(getShippingValue(order), 0));
  // Initialize tax based on billing country: 15% of (subtotal + shipping) for Mauritius, 0% for others
  // Always recalculate based on country, ignore order.tax_amount from backend
  const initialTax = useMemo(() => {
    const billingCountry = order?.billing_country || '';
    // Admin portal: for tax calculation, use combined shipping (base + additional)
    // Use shipping_base_cost if available, otherwise fallback to shipping_cost
    const shippingBaseCost = num(order?.shipping_base_cost && order.shipping_base_cost > 0 ? order.shipping_base_cost : (order?.shipping_cost || 0));
    const shippingAdditionalCost = num(order?.shipping_additional_cost || 0);
    const shippingCost = shippingBaseCost + shippingAdditionalCost;
    if (billingCountry.toLowerCase() === 'mauritius') {
      return (computedSubtotal + shippingCost) * 0.15; // 15% of (subtotal + shipping) for Mauritius
    }
    return 0; // 0% tax for all other countries
  }, [order?.billing_country, order?.shipping_base_cost, order?.shipping_additional_cost, computedSubtotal]);
  const [tax, setTax] = useState(toMoneyInput(initialTax, 0));

  // Calculate total shipping from all shipments using Buyer's Shipping Value
  // Order Summary Shipping = Sum of all shipments' Buyer's Shipping Value
  // Buyer's Shipping Value already includes 10% margin, so just sum them (no extra calculation)
  // This will refresh automatically when shipment headers are updated (increase/decrease)
  // Exclude shipments with no available items (all items excluded or unavailable)
  const totalShipmentShipping = useMemo(() => {
    if (shipments.length === 0) {
      // Fallback to order shipping if no shipments
      return calculatedShipping !== null ? calculatedShipping : num(getShippingValue(order));
    }
    
    // Filter out shipments with no available items
    const availableShipments = shipments.filter(shipment => {
      if (!shipment.items || !order?.items) return false;
      // Check if shipment has at least one available item
      const shipmentItemIds = shipment.items.map(si => si.order_item_id);
      const shipmentOrderItems = order.items.filter(item => 
        shipmentItemIds.includes(item.id)
      );
      return shipmentOrderItems.length > 0 && shipmentOrderItems.some(item => {
        const availability = item.availability || 'available';
        const orderItemStatus = item.order_item_status || 'include';
        return availability !== 'unavailable' && orderItemStatus !== 'exclude';
      });
    });
    
    // Calculate from current shipment values (refreshes with shipment headers)
    const total = availableShipments.reduce((sum, shipment) => {
      // If buyer_shipping_value is saved (from Validate Order), use it
      // Otherwise calculate Buyer's Shipping Value: (Admin Reference + additional_cost) / 0.9
      // Admin Reference = shipping_base_cost + shipping_additional_cost
      if (shipment.buyer_shipping_value && shipment.buyer_shipping_value > 0) {
        console.log(`[OrderSummary] Shipment ${shipment.id}: using saved buyer_shipping_value=${shipment.buyer_shipping_value} (from Validate Order)`);
        return sum + num(shipment.buyer_shipping_value);
      }
      
      // Calculate Buyer's Shipping Value from current shipment values
      // Single formula for every vendor: (base + shipping_adj + additional) gets 10% margin
      // applied (x/0.9), then express_fee is added without margin. express_fee is now stored
      // per-shipment on the backend; for old shipments it's 0, falling back to the historical
      // Lenovo/LCR hardcoded behavior so old orders display unchanged.
      const shipmentBase = num(shipment.shipping_base_cost) || 0;
      const shipmentAdjShipping = num(shipment.shipping_additional_cost) || 0;  // From increase/decrease
      const shipmentAdditionalCost = num(shipment.additional_cost) || 0;  // Separate additional cost

      // MFP freight-matrix fallback: when no base is stored yet — e.g. a mixed cart
      // where one item's category is unmapped, so order creation deferred the whole
      // MFP shipment — the backend still computes the AVAILABLE matrix value as
      // calculated_shipping (= matrix base × 1.10). Use it so the shipping we did
      // compute is shown in the total instead of being dropped. Display-only, MFP-scoped.
      const isMfp = (shipment.vendor || '').toLowerCase().includes('mfp');
      const matrixBase = (isMfp && shipmentBase === 0 && num(shipment.calculated_shipping) > 0)
        ? (num(shipment.calculated_shipping) / 1.10)
        : 0;
      const effectiveBase = shipmentBase > 0 ? shipmentBase : matrixBase;

      const backendExpressFee = num(shipment.express_fee);
      let expressFee = backendExpressFee;
      if (backendExpressFee === 0) {
        // Pre-express_fee-column shipments: keep the old hardcoded behavior so totals
        // for historical Lenovo/LCR orders don't shift after deploy.
        const vendorLower = (shipment.vendor || '').toLowerCase();
        const isLcr = vendorLower === 'lcr';
        const isLenovoTopChoice = vendorLower.includes('lenovo-top-choice') || vendorLower === 'lenovo top choice';
        if (isLcr || (isLenovoTopChoice && order?.priority === 'URGENT')) {
          expressFee = 215.0;
        }
      }

      // Selling price (10% gross margin): customer pays X where vendor cost == 0.9·X.
      // Express fee is passed through with no margin.
      const baseWithAdj = effectiveBase + shipmentAdjShipping;
      const dhlSelling = baseWithAdj > 0 ? (baseWithAdj / 0.9) : 0;
      const additionalSelling = shipmentAdditionalCost > 0 ? (shipmentAdditionalCost / 0.9) : 0;
      const buyerValue = dhlSelling + expressFee + additionalSelling;

      console.log(`[OrderSummary] Shipment ${shipment.id}: calculated buyer_shipping_value=${buyerValue.toFixed(2)} (base=${shipmentBase}, adj=${shipmentAdjShipping}, additional=${shipmentAdditionalCost}, express=${expressFee}, vendor=${shipment.vendor})`);
      return sum + buyerValue;
    }, 0);
    
    console.log(`[OrderSummary] Total shipping (buyer's view) from ${availableShipments.length} available shipments (out of ${shipments.length} total): ${total.toFixed(2)}`);
    return total;
  }, [shipments, calculatedShipping, order]);
  
  // Force recalculation when shipments change by creating a dependency on shipment values
  // This ensures Order Summary refreshes when shipment headers are updated
  const shipmentsSignature = useMemo(() => {
    return JSON.stringify(shipments.map(s => ({
      id: s.id,
      shipping_base_cost: s.shipping_base_cost,
      shipping_additional_cost: s.shipping_additional_cost,
      additional_cost: s.additional_cost,
      buyer_shipping_value: s.buyer_shipping_value
    })));
  }, [shipments]);

  useEffect(() => {
    const s = computedSubtotal;
    // Use total shipment shipping (sum of all shipments' shipping + additional costs)
    const sh = totalShipmentShipping;
    setSubtotal(toMoneyInput(s, 0));
    setShipping(toMoneyInput(sh, 0));
    // Tax calculation based on billing country:
    // - If billing country is Mauritius: 15% of (subtotal + shipping)
    // - For all other countries: 0%
    // Always recalculate based on country, ignore order.tax_amount from backend
    // Admin portal: use total shipment shipping (already includes all shipments' shipping + additional costs)
    const billingCountry = order?.billing_country || '';
    const combinedShipping = sh; // totalShipmentShipping already includes all shipments' shipping + additional costs
    let calculatedTax = 0;
    if (billingCountry.toLowerCase() === 'mauritius') {
      calculatedTax = (s + combinedShipping) * 0.15; // 15% of (subtotal + shipping) for Mauritius
    } else {
      calculatedTax = 0; // 0% tax for all other countries
    }
    setTax(toMoneyInput(calculatedTax, 0));
    console.log(`[OrderSummary] useEffect triggered - Subtotal: ${s}, Shipping: ${sh}, Tax: ${calculatedTax}, ShipmentsSignature: ${shipmentsSignature.substring(0, 50)}...`);
  }, [computedSubtotal, totalShipmentShipping, order?.billing_country, shipmentsSignature]);

  // Update shipping from DB when order changes (but not when items change - that's handled separately)
  // NOTE: We now use totalShipmentShipping (sum of all shipments' Buyer's Shipping Value) instead of order.shipping_cost
  // This is handled in the useEffect that depends on totalShipmentShipping (line 258)
  // Only fallback to order.shipping_cost if no shipments exist (handled in totalShipmentShipping calculation)
  useEffect(() => {
    // Only update if shipments are not loaded yet (fallback case)
    // Once shipments are loaded, totalShipmentShipping will be used (line 258-279)
    if (calculatedShipping === null && order && shipments.length === 0) {
      setShipping(toMoneyInput(getShippingValue(order), 0));
    }
  }, [order?.shipping_base_cost, order?.shipping_cost, calculatedShipping, shipments.length]);

  // Initialize shipping base cost from order when it changes
  useEffect(() => {
    if (order?.shipping_base_cost !== undefined && order?.shipping_base_cost !== null) {
      // Don't set newShippingBaseCost here - that's for user input
      // The read-only field will show order.shipping_base_cost directly
    }
  }, [order?.shipping_base_cost]);

  // Initialize shipping additional cost from order when it changes
  useEffect(() => {
    if (order?.shipping_additional_cost !== undefined && order?.shipping_additional_cost !== null) {
      // Don't set newShippingAdditionalCost here - that's for user input
      // The read-only field will show order.shipping_additional_cost directly
    }
  }, [order?.shipping_additional_cost]);

  // Removed useEffect that was overriding tax with order.tax_amount
  // Tax is now always calculated based on billing country (Mauritius = 15%, others = 0%)

  // Calculate shipping ONLY when items actually change (add/remove/update qty)
  // Not on page refresh or order.id change
  const prevItemsRef = useRef(null);
  useEffect(() => {
    if (!order?.id) return;
    
    // Get current items signature (for comparison)
    const currentItemsSignature = JSON.stringify(
      (order?.items || []).map(item => ({
        id: item.id,
        quantity: item.quantity,
        product_id: item.product_id
      })).sort((a, b) => (a.id || 0) - (b.id || 0))
    );
    
    // Only calculate if items actually changed (different from previous)
    const prevSignature = prevItemsRef.current;
    const itemsChanged = prevSignature !== currentItemsSignature;
    
    if (itemsChanged && prevSignature !== null) {
      // Items changed - calculate shipping and update DB
      const calculateShipping = async () => {
        try {
          const shippingResult = await calculateOrderShipping(order.id);
          const calculatedCost = shippingResult.total_shipping_cost || 0;
          setCalculatedShipping(calculatedCost);
          // Store full breakdown for info button
          setShippingBreakdown(shippingResult);
          
          // Update shipping_base_cost in DB via API
          try {
            await updateOrderShippingBaseCost(order.id, calculatedCost);
            // Refresh order to get updated values
            if (onOrderChanged) {
              const updatedOrder = await getOrderById(order.id);
              onOrderChanged(updatedOrder);
            }
          } catch (updateError) {
            console.error('Error updating shipping in DB:', updateError);
            // Don't show error - calculation succeeded, just DB update failed
          }
        } catch (error) {
          console.error('Error calculating shipping:', error);
          // Don't show error toast for shipping calculation failures
          setShippingBreakdown(null);
        }
      };
      
      calculateShipping();
    } else if (prevSignature === null) {
      // First load - reset calculatedShipping to null so we show DB value
      setCalculatedShipping(null);
    }
    
    // Update ref for next comparison
    prevItemsRef.current = currentItemsSignature;
  }, [order?.id, order?.items]);

  const billable = useMemo(() => {
    // If any ON_DEMAND items exist, return null to indicate "Pending"
    if (hasOnDemandItems) {
      return null;
    }
    const s = parseFloat(subtotal || 0);
    // Use totalShipmentShipping (sum of all shipments' shipping_cost + additional_cost) instead of order-level shipping
    const sh = parseFloat(totalShipmentShipping || 0);
    const t = parseFloat(tax || 0);
    const disc = parseFloat(order?.discount_amount || 0);
    return toMoneyInput((s || 0) + (sh || 0) + (t || 0) - disc, 0);
  }, [subtotal, tax, totalShipmentShipping, order?.discount_amount, hasOnDemandItems]);

  // Emit the billable to the parent (OrderStatusFinal) so credit-dialog and
  // any other consumer can use the same number that the sidebar shows.
  useEffect(() => {
    if (typeof onBillableChange === 'function') {
      const num = billable === null ? null : parseFloat(billable);
      onBillableChange(Number.isFinite(num) ? num : null);
    }
  }, [billable, onBillableChange]);

  const [busy] = useState(false); // payment button currently hidden

  const handleClickToPay = async () => {
    if (!order?.id) {
      alert("Order id not found");
      return;
    }
    // (kept for future use; button is commented out)
    // ...
  };

  const handleUpdateShipping = async () => {
    if (!order?.id) {
      showToast("Order ID not found.", "error");
      return;
    }

    const shippingCost = parseFloat(newShippingAmount);
    if (isNaN(shippingCost) || shippingCost < 0) {
      showToast("Please enter a valid shipping amount.", "warning");
      return;
    }

    setUpdatingShipping(true);
    try {
      const updatedOrder = await updateOrderShipping(order.id, shippingCost);
      setNewShippingAmount("");
      if (onOrderChanged) onOrderChanged(updatedOrder);
      showToast("Shipping cost updated successfully!", "success");
    } catch (error) {
      console.error("Failed to update shipping:", error);
      showToast("Failed to update shipping cost. Please try again.", "error");
    } finally {
      setUpdatingShipping(false);
    }
  };

  const handleUpdateTax = async () => {
    if (!order?.id) {
      showToast("Order ID not found.", "error");
      return;
    }

    const taxAmount = parseFloat(newTaxAmount);
    if (isNaN(taxAmount) || taxAmount < 0) {
      showToast("Please enter a valid VAT amount.", "warning");
      return;
    }

    setUpdatingTax(true);
    try {
      const updatedOrder = await updateOrderTax(order.id, taxAmount);
      setNewTaxAmount("");
      if (onOrderChanged) onOrderChanged(updatedOrder);
      showToast("VAT amount updated successfully!", "success");
    } catch (error) {
      console.error("Failed to update tax:", error);
      showToast("Failed to update tax amount. Please try again.", "error");
    } finally {
      setUpdatingTax(false);
    }
  };

  const handleUpdateShippingBaseCost = async () => {
    if (!order?.id) {
      showToast("Order ID not found.", "error");
      return;
    }

    const adjustment = parseFloat(newShippingBaseCost);
    if (isNaN(adjustment)) {
      showToast("Please enter a valid amount (+ or -).", "warning");
      return;
    }

    // Calculate new shipping_base_cost (add/subtract adjustment)
    const currentBaseCost = order?.shipping_base_cost || 0;
    const newBaseCost = currentBaseCost + adjustment;

    // Ensure final shipping_base_cost is not negative
    if (newBaseCost < 0) {
      showToast("Shipping base cost cannot be negative. Current value: " + toMoneyInput(currentBaseCost, 0), "warning");
      return;
    }

    setUpdatingShippingBaseCost(true);
    try {
      await updateOrderShippingBaseCost(order.id, newBaseCost);
      setNewShippingBaseCost("");
      
      // Fetch updated order to refresh UI with latest values
      if (onOrderChanged) {
        const refreshedOrder = await getOrderById(order.id);
        onOrderChanged(refreshedOrder);
      }
      
      const adjustmentText = adjustment >= 0 ? `increased by ${toMoneyInput(adjustment, 0)}` : `decreased by ${toMoneyInput(Math.abs(adjustment), 0)}`;
      showToast(`Shipping base cost ${adjustmentText} successfully!`, "success");
    } catch (error) {
      console.error("Failed to update shipping base cost:", error);
      showToast("Failed to update shipping base cost. Please try again.", "error");
    } finally {
      setUpdatingShippingBaseCost(false);
    }
  };

  const handleUpdateShippingAdditionalCost = async () => {
    if (!order?.id) {
      showToast("Order ID not found.", "error");
      return;
    }

    const shippingAdditionalCost = parseFloat(newShippingAdditionalCost);
    if (isNaN(shippingAdditionalCost) || shippingAdditionalCost < 0) {
      showToast("Please enter a valid shipping additional cost amount.", "warning");
      return;
    }

    setUpdatingShippingAdditionalCost(true);
    try {
      await updateOrderShippingAdditionalCost(order.id, shippingAdditionalCost);
      setNewShippingAdditionalCost("");
      
      // Fetch updated order to refresh UI with latest values
      if (onOrderChanged) {
        const refreshedOrder = await getOrderById(order.id);
        onOrderChanged(refreshedOrder);
      }
      
      showToast("Shipping additional cost updated successfully!", "success");
    } catch (error) {
      console.error("Failed to update shipping additional cost:", error);
      showToast("Failed to update shipping additional cost. Please try again.", "error");
    } finally {
      setUpdatingShippingAdditionalCost(false);
    }
  };

  return (
    <PageContainer>
      {/* Shipment Details - Vendor-wise split */}
      {order?.id && (
        <Box sx={{ mt: 0, mb: 3 }}>
          <HeaderTitle variant="h6" sx={{ mb: 2 }}>
            Shipments
            {orderShipmentCount !== null && orderShipmentCount > 0 && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 'normal', fontStyle: 'italic' }}>
                {orderShipmentCount > 1 
                  ? `(Order has been split into ${orderShipmentCount} shipments)`
                  : '(Order will be shipped in a single shipment.)'
                }
              </Typography>
            )}
          </HeaderTitle>
          <ShipmentDetails 
            orderId={order.id} 
            orderItems={order.items} 
            isAdmin={true} 
            orderStatus={order?.status}
            orderPriority={order?.priority}
            onShipmentCountChange={setOrderShipmentCount}
            onShipmentsUpdated={fetchShipmentsForSummary}
            readOnly={readOnly}
          />
        </Box>
      )}

      <Grid container spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Grid item xs={12} md="auto">
          <HeaderTitle variant="h6">System</HeaderTitle>
          <SystemField
            fullWidth
            variant="outlined"
            value={systemNote}
            onChange={(e) => setSystemNote(e.target.value)}
            disabled={readOnly}
          />
        </Grid>

        <Grid item xs={12} md="auto">
          <SummaryBox>
            <SummaryRow>
              <SummaryLabel>Subtotal</SummaryLabel>
              <SummaryValue
                aria-label="Subtotal"
                variant="outlined"
                value={subtotal}
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiInputBase-input": { backgroundColor: "#f5f5f5", cursor: "not-allowed" } }}
              />
            </SummaryRow>

            <SummaryRow>
              <SummaryLabel sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                Shipping (Includes 10% Margin)
                {shippingBreakdown?.breakdown?.length > 0 && (
                    <Tooltip
                      title={
                        <Box sx={{ 
                          maxWidth: 400, 
                          p: 2, 
                          backgroundColor: 'white', 
                          borderRadius: 2,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          border: '1px solid #e0e0e0'
                        }}>
                          {shippingBreakdown.has_manual_quotes && shippingBreakdown.total_shipping_cost === 0 ? (
                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666' }}>
                              Shipping will be calculated once order is confirmed
                            </Typography>
                          ) : (() => {
                            // Calculate remaining shipping for "to be calculated" items
                            // Admin portal: show combined shipping in tooltip breakdown
                            // Note: shipping_base_cost already includes 10% margin from cart
                            // breakdown.cost values from API also include 10% margin now
                            const finalShipping = (order?.shipping_base_cost || 0) + (order?.shipping_additional_cost || 0);
                            // breakdown.cost values already include 10% margin from backend API
                            const calculatedShipping = shippingBreakdown.breakdown
                              .filter(item => !item.message?.includes('To be calculated') && item.cost > 0)
                              .reduce((sum, item) => sum + (item.cost || 0), 0);
                            const remainingShipping = finalShipping - calculatedShipping;
                            const toBeCalculatedGroups = shippingBreakdown.breakdown.filter(
                              item => item.message?.includes('To be calculated')
                            );
                            const remainingPerGroup = toBeCalculatedGroups.length > 0 
                              ? remainingShipping / toBeCalculatedGroups.length 
                              : 0;
                            
                            // Get order status
                            const orderStatus = order?.status || '';
                            const isOrderValidated = orderStatus === 'AWAITING PAYMENT' || 
                                                     orderStatus === 'PAID' || 
                                                     orderStatus === 'VENDOR ORDER PROCESSED' || 
                                                     orderStatus === 'ASSEMBLING ORDER' || 
                                                     orderStatus === 'SHIPPED' || 
                                                     orderStatus === 'DELIVERED';
                            
                            // Group order items by their shipping breakdown group
                            const orderItemsWithShipping = (order?.items || []).map(orderItem => {
                              // Find matching shipping breakdown group
                              // Try multiple sources for vendor and category
                              const vendor = orderItem.product?.vendor || 
                                            orderItem.snapshot_product_vendor || 
                                            orderItem.product?.brand ||
                                            orderItem.snapshot_product_brand;
                              const category = orderItem.product?.category || 
                                              orderItem.snapshot_product_category;
                              
                              const orderItemDescription = orderItem.snapshot_product_description || 
                                                           orderItem.product_name || 
                                                           orderItem.product?.description || 
                                                           '';
                              
                              // Normalize vendor name (EXACT same logic as backend ShippingCalculator._normalize_vendor_name)
                              const normalizeVendor = (v) => {
                                if (!v) return v;
                                
                                const vLower = v.toLowerCase().trim();
                                
                                // Backend vendor mappings
                                const vendorMappings = {
                                  'mfp-technology': 'MFP Technology',
                                  'mfp_technology': 'MFP Technology',
                                  'mfp technology': 'MFP Technology',
                                  'sourcing-it': 'Sourcing IT',
                                  'sourcing_it': 'Sourcing IT',
                                  'sourcing it': 'Sourcing IT',
                                  'advent-data-solution': 'Advent Data Solution',
                                  'advent_data_solution': 'Advent Data Solution',
                                  'advent data solution': 'Advent Data Solution',
                                  'sla-expert': 'SLA Expert',
                                  'sla_expert': 'SLA Expert',
                                  'sla expert': 'SLA Expert'
                                };
                                
                                // Direct mapping
                                if (vLower in vendorMappings) {
                                  return vendorMappings[vLower];
                                }
                                
                                // If no mapping found, return original vendor (same as backend)
                                return v;
                              };
                              
                              // Map category to group (EXACT same logic as backend ShippingCalculator._map_category_to_group)
                              const mapCategoryToGroup = (cat) => {
                                if (!cat) return 'Small Components';  // Backend default
                                
                                const catLower = cat.toLowerCase().trim();
                                
                                // Backend CATEGORY_MAPPING
                                const categoryMapping = {
                                  'adapter': 'Small Components',
                                  'raid': 'Small Components',
                                  'hba': 'Small Components',
                                  'ssd': 'Small Components',
                                  'hdd': 'Small Components',
                                  'hard drive': 'Small Components',
                                  'fans': 'Small Components',
                                  'processor': 'Small Components',
                                  'sfp': 'Small Components',
                                  'cables': 'Small Components',
                                  'memory': 'Small Components',
                                  'heatsinks': 'Small Components',
                                  'power supplies': 'Small Components',
                                  'power supply': 'Small Components',
                                  'mice': 'Small Components',
                                  'risers': 'Small Components',
                                  'gpu': 'Small Components',
                                  'network adapter': 'Small Components',
                                  'server': 'Large Equipment',
                                  'servers': 'Large Equipment',
                                  'storage': 'Large Equipment',
                                  'monitor': 'Large Equipment',
                                  'monitors': 'Large Equipment',
                                  'tape enclosure': 'Large Equipment',
                                  'tape enclosures': 'Large Equipment',
                                  'motherboard': 'Medium Components',
                                  'motherboards': 'Medium Components',
                                  'expansion board': 'Medium Components',
                                  'expansion boards': 'Medium Components',
                                  'tape drive': 'Medium Components',
                                  'tape drives': 'Medium Components',
                                  'switch': 'Switches',
                                  'switches': 'Switches',
                                  'keyboard': 'Keyboards',
                                  'keyboards': 'Keyboards',
                                  'pdu': 'PDUs',
                                  'pdus': 'PDUs',
                                  'rail kit': 'Rail Kits',
                                  'rack kit': 'Rail Kits'
                                };
                                
                                // Direct mapping
                                if (catLower in categoryMapping) {
                                  return categoryMapping[catLower];
                                }
                                
                                // Partial matching (same as backend)
                                for (const [key, group] of Object.entries(categoryMapping)) {
                                  if (catLower.includes(key)) {
                                    return group;
                                  }
                                }
                                
                                // Default fallback (same as backend)
                                return 'Small Components';
                              };
                              
                              const normalizedVendor = normalizeVendor(vendor);
                              const categoryGroup = mapCategoryToGroup(category);
                              
                              // First try: Match by vendor + category
                              let matchingBreakdown = shippingBreakdown.breakdown.find(bd => {
                                const bdVendor = (bd.vendor || '').toLowerCase().trim();
                                const bdCategory = (bd.category_group || '').toLowerCase().trim();
                                const orderVendor = (normalizedVendor || '').toLowerCase().trim();
                                const orderCategory = (categoryGroup || '').toLowerCase().trim();
                                return bdVendor === orderVendor && bdCategory === orderCategory;
                              });
                              
                              // Fallback: If vendor is missing, match by description from breakdown items
                              if (!matchingBreakdown && orderItemDescription) {
                                matchingBreakdown = shippingBreakdown.breakdown.find(bd => {
                                  if (!bd.items || !Array.isArray(bd.items)) return false;
                                  return bd.items.some(bdItem => {
                                    const bdDescription = (bdItem.description || '').toLowerCase().trim();
                                    const orderDesc = orderItemDescription.toLowerCase().trim();
                                    return bdDescription === orderDesc || 
                                           bdDescription.includes(orderDesc) || 
                                           orderDesc.includes(bdDescription);
                                  });
                                });
                              }
                              
                              return {
                                orderItem,
                                breakdown: matchingBreakdown,
                                productName: orderItem.snapshot_product_description || 
                                            orderItem.product_name || 
                                            orderItem.product?.description || 
                                            'Unknown Product'
                              };
                            });
                            
                            // Group by shipping breakdown to avoid duplicates
                            const groupedByShipping = {};
                            orderItemsWithShipping.forEach(({ orderItem, breakdown, productName }) => {
                              const key = breakdown ? `${breakdown.vendor}_${breakdown.category_group}` : `unknown_${orderItem.id}`;
                              if (!groupedByShipping[key]) {
                                groupedByShipping[key] = {
                                  breakdown,
                                  items: [],
                                  totalQuantity: 0
                                };
                              }
                              groupedByShipping[key].items.push({ orderItem, productName });
                              groupedByShipping[key].totalQuantity += orderItem.quantity || 1;
                            });
                            
                            return (
                              <>
                                {/* Total Items Header */}
                                <Typography variant="h6" sx={{ 
                                  mb: 2, 
                                  fontWeight: 'bold', 
                                  color: '#333',
                                  borderBottom: '1px solid #e0e0e0',
                                  pb: 1
                                }}>
                                  Total Items: {order?.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
                                </Typography>
                                
                                {Object.values(groupedByShipping).map((group, index) => {
                                  // Check if breakdown exists
                                  if (!group.breakdown) {
                                    return (
                                      <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < Object.keys(groupedByShipping).length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                        <Typography variant="body2" sx={{ mb: 1, fontSize: '0.9rem', lineHeight: 1.4, whiteSpace: 'pre-line', color: '#555' }}>
                                          {group.items.map((item, idx) => 
                                            `${idx + 1}. ${item.productName} (${item.orderItem.quantity || 1})`
                                          ).join('\n')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333', display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <span>Shipping:</span>
                                          <span>To be calculated</span>
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                  
                                  const breakdown = group.breakdown;
                                  
                                  // ONLY check message for "To be calculated" - this is the reliable indicator
                                  const isToBeCalculated = breakdown.message?.includes('To be calculated') || false;
                                  
                                  // If cost > 0, it's calculated shipping (from matrix or DHL API)
                                  const hasCalculatedShipping = breakdown.cost > 0;
                                  
                                  let shippingDisplay = 'To be calculated';
                                  
                                  // If item has calculated shipping (cost > 0), always show actual value
                                  // breakdown.cost already includes 10% margin from backend API
                                  if (hasCalculatedShipping) {
                                    shippingDisplay = breakdown.message || `$${Number(breakdown.cost || 0).toFixed(2)}`;
                                  }
                                  // If item is "to be calculated" (message says so) and order is validated, show remaining shipping
                                  else if (isToBeCalculated && isOrderValidated && finalShipping > 0 && remainingPerGroup > 0) {
                                    shippingDisplay = `$${Number(remainingPerGroup).toFixed(2)}`;
                                  }
                                  // Otherwise show "To be calculated"
                                  else if (isToBeCalculated) {
                                    shippingDisplay = 'To be calculated';
                                  }
                                  
                                  return (
                                    <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < Object.keys(groupedByShipping).length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                      {/* Items List */}
                                      <Typography variant="body2" sx={{ 
                                        mb: 1, 
                                        fontSize: '0.9rem', 
                                        lineHeight: 1.4, 
                                        whiteSpace: 'pre-line',
                                        color: '#555'
                                      }}>
                                        {group.items.map((item, idx) => 
                                          `${idx + 1}. ${item.productName} (${item.orderItem.quantity || 1})`
                                        ).join('\n')}
                                      </Typography>
                                      
                                      {/* Shipping Cost */}
                                      <Typography variant="body2" sx={{ 
                                        fontWeight: 'bold', 
                                        color: isToBeCalculated && !isOrderValidated ? '#ff9800' : '#333',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                      }}>
                                        <span>Shipping:</span>
                                        <span>{shippingDisplay}</span>
                                      </Typography>
                                    </Box>
                                  );
                                })}
                                
                                {/* Show overall asterisk message if any manual quotes and not yet validated */}
                                {shippingBreakdown.has_manual_quotes && !isOrderValidated && (
                                  <Typography variant="caption" sx={{ 
                                    color: '#ff9800', 
                                    fontSize: '0.75rem',
                                    fontStyle: 'italic',
                                    display: 'block',
                                    mt: 1,
                                    pt: 1,
                                    borderTop: '1px solid #f0f0f0'
                                  }}>
                                    * Final shipping amount will be calculated post order confirmation
                                  </Typography>
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <IconButton size="small" sx={{ p: 0.5 }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
              </SummaryLabel>
              <SummaryValue
                aria-label="Shipping"
                variant="outlined"
                value={shipping}
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiInputBase-input": { backgroundColor: "#f5f5f5", cursor: "not-allowed" } }}
              />
            </SummaryRow>

            <SummaryRow>
              <SummaryLabel>Discount</SummaryLabel>
              <SummaryValue
                aria-label="Discount"
                variant="outlined"
                value={toMoneyInput(order?.discount_amount || 0, 0)}
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiInputBase-input": { backgroundColor: "#f5f5f5", cursor: "not-allowed" } }}
              />
            </SummaryRow>

            <SummaryRow>
              <SummaryLabel>VAT</SummaryLabel>
              <SummaryValue
                aria-label="VAT"
                variant="outlined"
                value={tax}
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiInputBase-input": { backgroundColor: "#f5f5f5", cursor: "not-allowed" } }}
              />
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel></SummaryLabel>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  label="Enter new VAT amount"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={newTaxAmount}
                  onChange={(e) => setNewTaxAmount(e.target.value)}
                  sx={{ width: 200 }}
                  inputProps={{ min: 0, step: 0.01 }}
                  disabled={isShippedOrDelivered || disableButtons}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleUpdateTax}
                  disabled={updatingTax || !newTaxAmount || isShippedOrDelivered || disableButtons}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {updatingTax ? "Updating..." : "Update VAT"}
                </Button>
              </Box>
            </SummaryRow>

            <SummaryRow>
              <SummaryLabel>Billable</SummaryLabel>
              <TextField
                aria-label="Billable"
                variant="outlined"
                value={billable === null ? "Pending" : billable}
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ 
                  width: 140,
                  "& .MuiInputBase-input": { 
                    backgroundColor: "#f5f5f5", 
                    cursor: "not-allowed",
                    color: billable === null ? '#ff9800' : 'inherit',
                    fontWeight: billable === null ? 600 : 'normal'
                  }
                }}
              />
            </SummaryRow>

            {Number(order?.credit_amount || 0) > 0 && (
              <>
                <SummaryRow>
                  <SummaryLabel sx={{ color: '#2e7d32' }}>Credit Applied</SummaryLabel>
                  <TextField
                    aria-label="Credit Applied"
                    variant="outlined"
                    value={`-$${Number(order.credit_amount).toFixed(2)}`}
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ width: 140, "& .MuiInputBase-input": { backgroundColor: "#e8f5e9", cursor: "not-allowed", color: '#2e7d32', fontWeight: 600 } }}
                  />
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel sx={{ fontWeight: 700 }}>Balance Due</SummaryLabel>
                  <TextField
                    aria-label="Balance Due"
                    variant="outlined"
                    value={`$${Math.max(0, (billable === null ? Number(order.total_amount || 0) : parseFloat(billable)) - Number(order.credit_amount)).toFixed(2)}`}
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ width: 140, "& .MuiInputBase-input": { backgroundColor: "#e3f2fd", cursor: "not-allowed", fontWeight: 700 } }}
                  />
                </SummaryRow>
              </>
            )}

            {/* Payment button kept hidden for now
            <PaymentButton
              variant="contained"
              fullWidth
              disabled={busy}
              onClick={handleClickToPay}
            >
              {busy ? "Creating Link…" : "CLICK TO PAY"}
            </PaymentButton> */}
          </SummaryBox>
        </Grid>
      </Grid>
    </PageContainer>
  );
}
